import { type JsonSchema7 } from '@jsonforms/core'

import { isLauncherJourney } from '../../navigation'
import type { AtLeast } from '../../types'
import * as next from '../../types/next'
import type { LogicString } from '../../utils'
import { getBlockNameByScope, logicStringsToObjects } from '../../utils'
import {
  isGroupUiSchema,
  isLayoutUiSchema,
  isNestedUiSchema,
  isBlockUiSchema
} from '../../utils/types/sharedTypes'
import type * as current from '../../utils/types/sharedTypes'

import { enrichBlock, upBlockControl } from './blocks'
import { MISSING_STRING, PREVIEW_JOURNEY_ID } from './constants'
import { upLogicControl } from './logics'
import {
  newId,
  blockIdMapper,
  findJourneyLauncherControl,
  MigrationStore
} from './utils'

/**
 * Transforms Journey schema from version 3 to version 4.
 *
 * If you have reasons to worry about potentially introducing issues, please manually run
 * the Journey migration tests (`packages/journey-logic-commons/src/schemas/v4/migrate-journeys.test.ts`)
 * to verify your changes against Production data before merging in.
 */
export const up = (source: current.Journey): next.Journey => {
  const input = prepareSource(source)

  const _id = input.journeyId ?? PREVIEW_JOURNEY_ID

  const baseConfig: next.IJourney = {
    id: _id,
    entityId: input.settings?.entityId ?? MISSING_STRING,
    organizationId: input.organizationId,
    version: input.version,
    revisions: input.revisions,
    type: upType(input),
    status: upJourneyStatus(input.settings?.status),
    name: input.name,
    createdBy: input.createdBy,
    createdAt: input.createdAt,
    lastModifiedAt: input.lastModifiedAt,
    deletedAt: input.deletedAt,
    isTemplate: _id === PREVIEW_JOURNEY_ID
  }

  switch (baseConfig.type) {
    case next.JOURNEY_TYPE.LAUNCHER:
      return {
        ...baseConfig,
        settings: upSettings<next.LauncherJourney>(input.settings, input),
        journeys: upLaunchableJourneys(input)
      } as next.LauncherJourney
    case next.JOURNEY_TYPE.REGULAR: {
      return {
        ...baseConfig,
        ...upJourneyContent(input),
        settings: upSettings<next.RegularJourney>(input.settings, input),
        firstStepId: input.steps[0]?.stepId,
        contextParameters: upContextParameters(input.contextSchema)
      } as next.RegularJourney
    }
    // In the current migration, we do not differentiate between regular and post-qualification journeys.
    case next.JOURNEY_TYPE.POST_QUALIFICATION:
      return {
        ...baseConfig,
        ...upJourneyContent(input),
        settings: upSettings<next.PostQualificationJourney>(
          input.settings,
          input
        ),
        firstStepId: input.steps[0]?.stepId,
        contextParameters: upContextParameters(input.contextSchema),
        linkedJoureys: []
      } as next.PostQualificationJourney

    default:
      throw new Error(`Unsupported Journey type: ${baseConfig.type}`)
  }
}

/**
 * Prepares a given Journey for transformation.
 * Useful to ensure that all necessary fields are present ahead of time.
 */
export const prepareSource = (source: current.Journey): current.Journey => {
  const prepared = structuredClone(source)

  // assign ids to context parameters
  if (prepared.contextSchema?.length) {
    for (const item of prepared.contextSchema) {
      item.id = newId<next.ContextParameterId>()
    }
  }

  // assign ids to steps
  if (prepared.steps?.length) {
    for (const step of prepared.steps) {
      if (typeof step.stepId === 'undefined') {
        step.stepId = newId<next.StepId>()
      }

      // assign ids to blocks
      step.uischema = prepareStepUischema(step)
    }
  }

  return prepared
}

export const prepareStepUischema = (
  step: AtLeast<current.Step, 'uischema'>
): (typeof step)['uischema'] => {
  const uischema = structuredClone(step.uischema)

  if (isNestedUiSchema(uischema)) {
    for (const zone of uischema.elements) {
      for (const element of zone) {
        if (isBlockUiSchema(element) && !element.id) {
          element.id = newId<next.BlockId>()
        } else if (isGroupUiSchema(element)) {
          if (!element.id) {
            element.id = newId<next.BlockId>()
          }

          for (const item of element.elements) {
            if (isBlockUiSchema(item) && !item.id) {
              item.id = newId<next.BlockId>()
            }
          }
        }
      }
    }
  } else if (isLayoutUiSchema(uischema)) {
    for (const element of uischema.elements) {
      if (isGroupUiSchema(element)) {
        if (!element.id) {
          element.id = newId<next.BlockId>()
        }

        for (const item of element.elements) {
          if (isBlockUiSchema(item) && !item.id) {
            item.id = newId<next.BlockId>()
          }
        }
      } else if (isBlockUiSchema(element) && !element.id) {
        element.id = newId<next.BlockId>()
      }
    }
  } else {
    throw new Error(
      `unreachable for step uischema: ${JSON.stringify(uischema, null, 2)}`
    )
  }

  return uischema
}

export const upJourneyContent = (
  input: current.Journey
): Pick<
  next.RegularJourney | next.PostQualificationJourney,
  'logics' | 'steps' | 'blocks'
> => {
  const { steps, blocks } = input.steps.reduce<{
    steps: Record<next.StepId, next.Step>
    blocks: Record<next.BlockId, next.Block>
  }>(
    (acc, step, stepIndex) => {
      // transform

      const stepId = step.stepId ?? newId<next.StepId>(Object.keys(acc.steps))

      const blocks = upBlocks(step.uischema, step.schema, input)

      let stepLayout: next.StepLayout

      if (isNestedUiSchema(step.uischema)) {
        stepLayout = upStepLayout(step.uischema) as next.TwoColumnLayout
        stepLayout.content.children = blockIdMapper(blocks[0])
        stepLayout.sidebar.children = blockIdMapper(blocks[1])
        stepLayout.header.children = blockIdMapper(blocks[2])
        stepLayout.footer.children = blockIdMapper(blocks[3])
      } else {
        // Stack Layout and Grid Layout do the same thing, just type changes
        stepLayout = upStepLayout(step.uischema)
        stepLayout.content.children = blockIdMapper(blocks[0])
      }

      // assign

      for (const [index, block] of blocks.flat().entries()) {
        acc.blocks[block.id] = {
          ...block,
          stepId,
          orderIndex: index,
          required: Boolean(step.schema.required?.includes(block.name))
        } as next.Block
      }

      acc.steps[stepId] = {
        id: stepId,
        name: step.name,
        orderIndex: stepIndex,
        settings: {
          hideNextButton: Boolean(step.hideNextButton),
          onNextHooks: step.onNextHooks,
          showStepName: Boolean(step.showStepName),
          showStepper: Boolean(step.showStepper),
          showStepperLabels: Boolean(step.showStepperLabels),
          showStepSubtitle: Boolean(step.showStepSubtitle),
          subTitle: step.subTitle ?? undefined,
          title: step.title ?? undefined
        },
        layout: stepLayout
      }

      return acc
    },
    { steps: {}, blocks: {} }
  )

  const logics =
    input.logics?.reduce<Record<next.LogicId, next.Logic>>(
      (acc, logicString) => {
        const logic = upLogic(logicString, input)

        if (logic) {
          const id = newId<next.LogicId>()

          // @ts-expect-error for now
          acc[id] = {
            id,
            ...logic
          }
        }

        return acc
      },
      {}
    ) ?? {}

  return { steps, blocks: enrichBlocks(blocks, steps), logics }
}

export const enrichBlocks = (
  blocks: Exclude<next.Journey['blocks'], undefined>,
  steps: Exclude<next.Journey['steps'], undefined>
) => {
  if (!blocks) return blocks

  return Object.entries(blocks).reduce<typeof blocks>(
    (acc, [blockId, block]) => {
      acc[blockId] = enrichBlock(block, steps, blocks)

      return acc
    },
    {}
  )

  return blocks
}

export const upLogic = (
  logicString: LogicString,
  sourceJourney: current.Journey
): Omit<next.Logic, 'id' | 'orderIndex'> | undefined => {
  const logic = logicStringsToObjects([logicString])[0]

  if (!logic.actions || !logic.conditions) {
    return undefined
  }

  if (logic.actions?.length > 1) {
    throw new Error(`Multiple actions not supported`)
  }

  if (logic.conditions?.length > 1) {
    throw new Error(`Multiple conditions not supported`)
  }

  const condition = logic.conditions[0]
  const action = logic.actions[0]

  if (!condition) {
    throw new Error(`Condition cannot be undefined`)
  }

  if (!action) {
    throw new Error(`Action cannot be undefined`)
  }

  if (!condition.conditionSource) {
    throw new Error(`Condition source is required`)
  }

  return upLogicControl(condition, action, sourceJourney)
}

export const upType = (journey: current.Journey): next.JOURNEY_TYPE => {
  const isLauncher = isLauncherJourney(journey.steps)

  if (isLauncher) return next.JOURNEY_TYPE.LAUNCHER

  return next.JOURNEY_TYPE.REGULAR
}

export const upLaunchableJourneys = (
  journey: current.Journey
): next.LauncherJourney['journeys'] => {
  const control = findJourneyLauncherControl(journey)

  if (!control) return {}

  const castedItems = control.options?.linkedJourneys as {
    id: string
    label: string
    icon: string | null
  }[]

  return (castedItems || [])?.reduce<next.LauncherJourney['journeys']>(
    (acc, item, index) => {
      acc[item.id] = {
        journeyId: item.id,
        nameOverride: item.label,
        icon: item.icon || undefined,
        orderIndex: index
      }

      return acc
    },
    {}
  )
}

export const upLauncherPlacementOnCard = (
  journey: current.Journey
): boolean => {
  return Boolean(findJourneyLauncherControl(journey)?.options?.showPaper)
}

export const upSettings = <T extends next.Journey>(
  settings: current.Journey['settings'],
  sourceJourney: current.Journey
): T['settings'] => {
  if (!settings) {
    throw new Error('Expected settings to be defined')
  }

  const baseSettings: next.BaseJourneySettings = {
    language: upJourneyLanguage(settings.embedOptions?.lang),
    description: settings.description,
    templateId: settings.templateId,
    embedOptions: settings.embedOptions && {
      mode: upJourneyEmbedMode(settings.embedOptions.mode),
      width: settings.embedOptions.width,
      topBar: Boolean(settings.embedOptions.topBar),
      button: settings.embedOptions.button,
      scrollToTop: Boolean(settings.embedOptions.scrollToTop),
      lang: upJourneyLanguage(settings.embedOptions.lang)
    },
    organizationSettings: settings.organizationSettings,
    accessMode: upJourneyAccessMode(settings.accessMode, sourceJourney),
    entityTags: settings.entityTags,
    designId: settings.designId,
    thirdPartyCookies: settings.thirdPartyCookies ?? true,
    useAustrianLabels: settings.useAustrianLabels ?? false,
    enableDarkMode: settings.enableDarkMode ?? false
  }

  const journeyType = upType(sourceJourney)

  switch (journeyType) {
    case next.JOURNEY_TYPE.LAUNCHER:
      return {
        ...baseSettings,
        placementOnCard: upLauncherPlacementOnCard(sourceJourney)
      }
    case next.JOURNEY_TYPE.REGULAR:
      return {
        ...baseSettings,
        purpose: upPurpose(settings.runtimeEntities),
        automationId: settings.automationId || MISSING_STRING,
        mappingConfigId: settings.mappingsAutomationId || MISSING_STRING,
        safeModeAutomation: Boolean(settings.safeModeAutomation),
        runtimeEntities: upRuntimeEntities(settings.runtimeEntities),
        targetedCustomer: settings.targetedCustomer,
        publicToken: settings.publicToken,
        filePurposes: settings.filePurposes,
        addressSuggestionsFileUrl: settings.addressSuggestionsFileUrl,
        addressSuggestionsFileId: settings.addressSuggestionsFileId,
        mappingsAutomationId: settings.mappingsAutomationId,
        savingProgress: settings.savingProgress
      }
    default:
      throw new Error(`Unsupported Journey type: ${journeyType}`)
  }
}

export const upRuntimeEntities = (
  entities: current.EntitiesRunTime[] | undefined
): next.RUNTIME_ENTITY[] => {
  return (
    entities?.reduce<next.RUNTIME_ENTITY[]>((acc, entity) => {
      switch (entity) {
        case undefined:
          break
        case 'ORDER':
          acc.push(next.RUNTIME_ENTITY.ORDER)
          break
        case 'OPPORTUNITY':
          acc.push(next.RUNTIME_ENTITY.OPPORTUNITY)
          break
        default:
          throw new Error(`Unknown runtime entity: ${entity}`)
      }

      return acc
    }, []) ?? []
  )
}

export const upPurpose = (
  entities: current.EntitiesRunTime[] | undefined
): next.JOURNEY_PURPOSE => {
  const nextEntities = upRuntimeEntities(entities)

  if (nextEntities.length === 0) {
    return next.JOURNEY_PURPOSE.OTHER
  }

  if (
    [next.RUNTIME_ENTITY.OPPORTUNITY, next.RUNTIME_ENTITY.ORDER].every(
      (entity) => nextEntities.includes(entity)
    )
  ) {
    return next.JOURNEY_PURPOSE.REQUEST_360
  }

  if (nextEntities[0] === next.RUNTIME_ENTITY.OPPORTUNITY) {
    return next.JOURNEY_PURPOSE.GENERAL_REQUESTS
  }

  if (nextEntities[0] === next.RUNTIME_ENTITY.ORDER) {
    return next.JOURNEY_PURPOSE.SELL_360
  }

  throw new Error(
    `Unable to determine the Journey purpose based on runtime entities: ${nextEntities.join(', ')}`
  )
}

export const upJourneyEmbedMode = (
  mode: current.EmbedOptions['mode'] | undefined
): next.JOURNEY_EMBED_MODE => {
  switch (mode) {
    case 'full-screen':
      return next.JOURNEY_EMBED_MODE.FULL_SCREEN
    case 'inline':
      return next.JOURNEY_EMBED_MODE.INLINE
    default:
      throw new Error(`Unknown embed mode: ${mode}`)
  }
}

export const upJourneyLanguage = (
  lang: current.EmbedOptions['lang'] | undefined
): next.LANGUAGE => {
  switch (lang) {
    case undefined:
    case 'de':
      return next.LANGUAGE.DE
    case 'en':
      return next.LANGUAGE.EN
    case 'fr':
      return next.LANGUAGE.FR
    default:
      throw new Error(`Unknown language: ${lang}`)
  }
}

export const upJourneyAccessMode = (
  mode: current.JourneyAccessMode | undefined,
  /**
   * This was previously used to override the access mode for launcher journeys
   * and make them always public.
   * @todo Consider in the future whether there's a use case for private launcher journeys,
   * and what should be the behaviour when there's a discrepancy between launcher and their linked journeys.
   */
  _context: current.Journey
): next.JOURNEY_ACCESS_MODE | undefined => {
  switch (mode) {
    case undefined:
    case 'PUBLIC':
      return next.JOURNEY_ACCESS_MODE.PUBLIC
    case 'PRIVATE':
      return next.JOURNEY_ACCESS_MODE.PRIVATE
    default:
      throw new Error(`Unknown access mode: ${mode}`)
  }
}

export const upJourneyStatus = (
  status: next.JOURNEY_STATUS | undefined
): next.JOURNEY_STATUS => {
  if (status === undefined) {
    return next.JOURNEY_STATUS.PUBLISHED // safety first
  }

  return status
}

export const upContextParameterType = (
  type: current.ContextItem['type']
): next.CONTEXT_PARAMETER_TYPE => {
  switch (type) {
    case 'String':
      return next.CONTEXT_PARAMETER_TYPE.TEXT
    case 'account':
      return next.CONTEXT_PARAMETER_TYPE.ACCOUNT
    case 'contact':
      return next.CONTEXT_PARAMETER_TYPE.CONTACT
    case 'contract':
      return next.CONTEXT_PARAMETER_TYPE.CONTRACT
    case 'opportunity':
      return next.CONTEXT_PARAMETER_TYPE.OPPORTUNITY
    case 'order':
      return next.CONTEXT_PARAMETER_TYPE.ORDER
    case 'request':
      return next.CONTEXT_PARAMETER_TYPE.REQUEST
    case 'message':
      // Also known as 'email'
      return next.CONTEXT_PARAMETER_TYPE.MESSAGE
    default:
      return next.CONTEXT_PARAMETER_TYPE.TEXT
  }
}

/**
 * Transforms a UI schema into a Step layout.
 *
 * WARNING: This method is not responsible for filling in the associated block ids.
 */
export const upStepLayout = (
  uischema: current.UiSchema | undefined
): next.StepLayout => {
  switch (uischema?.type) {
    case undefined:
    case 'MainLinearLayout':
    case 'StackSpacingLayout':
      return {
        type: next.LAYOUT_TYPE.STACK,
        content: {
          children: []
        }
      }
    case 'MainContentCartLayout':
      return {
        type: next.LAYOUT_TYPE.TWO_COLUMN,
        header: {
          children: []
        },
        content: {
          children: []
        },
        sidebar: {
          children: []
        },
        footer: {
          children: []
        }
      }
    case 'GridLayout':
      return {
        type: next.LAYOUT_TYPE.GRID,
        content: {
          children: []
        }
      }
    default:
      throw new Error(`Unknown uischema type: ${uischema?.type}`)
  }
}

export const upContextParameters = (
  items: current.ContextItem[] | undefined
): Record<next.ContextParameterId, next.ContextParameter> => {
  return (
    items?.reduce<ReturnType<typeof upContextParameters>>((acc, item) => {
      if (!item.id) {
        throw new Error('Context parameter must have an id')
      }

      acc[item.id] = {
        id: item.id,
        type: upContextParameterType(item.type),
        paramKey: item.paramKey,
        isRequired: Boolean(item.isRequired),
        shouldLoadEntity: Boolean(item.shouldLoadEntity)
      }

      return acc
    }, {}) ?? {}
  )
}

type EssentialBlock = Omit<next.Block, 'stepId' | 'orderIndex'>

export const upBlocks = (
  uischema: current.UiSchema | undefined,
  schema: JsonSchema7,
  sourceJourney: current.Journey,
  parent?: next.Block
): EssentialBlock[][] => {
  if (uischema) {
    if (isNestedUiSchema(uischema)) {
      return uischema.elements.map((elements) =>
        elements.flatMap((block) =>
          upBlocks(block, schema, sourceJourney, parent).flat()
        )
      )
    } else if (isGroupUiSchema(uischema)) {
      const group = upBlock(uischema, schema, sourceJourney, parent)

      return [
        [
          group,
          ...uischema.elements.flatMap((block) =>
            upBlock(block, schema, sourceJourney, group)
          )
        ]
      ]
    } else if (isLayoutUiSchema(uischema) && !isGroupUiSchema(uischema)) {
      // The condition here already recognizes GridLayout as valid
      return [
        uischema.elements.flatMap((block) =>
          upBlocks(block, schema, sourceJourney, parent).flat()
        )
      ]
    }

    return [[upBlock(uischema, schema, sourceJourney, parent) as next.Block]]
  }

  return [[]]
}

export const upBlock = (
  uischema: current.BlockUiSchema,
  schema: JsonSchema7,
  /**
   * Source Journey as additional context.
   * Accepts undefined for cases where it isn't used by the underlying up function.
   */
  sourceJourney?: current.Journey,
  parent?: EssentialBlock
): EssentialBlock => {
  if (parent && parent.id === undefined) {
    throw new Error('Parent block must have an id')
  }

  if (parent && isGroupUiSchema(uischema)) {
    throw new Error('Groups cannot be nested')
  }

  const id = uischema.id ?? newId<next.BlockId>()

  if (
    parent &&
    parent.type === next.BLOCK_TYPE.GROUP &&
    (
      (parent as Omit<next.GroupBlock, 'stepId' | 'orderIndex'>).children ?? []
    ).includes(id) === false
  ) {
    throw new Error('Parent block does not reference this block as children')
  }

  const name = String(
    getBlockNameByScope(uischema.scope) ?? uischema.options?.title
  )

  const block = {
    id,
    name,
    parent: parent?.id,
    required: Boolean(schema.required?.includes(name)),
    ...upBlockControl(uischema.type, uischema, schema, sourceJourney)
  }

  // Store block so that logics can use it later
  const store = MigrationStore.getInstance()

  store.addBlock(id, block)

  return block
}
